// src/contexts/StagedUploadsContext.tsx
import React from 'react';

export type StagedMap = Map<string, File>; // nodeId -> File

type Ctx = {
  staged: StagedMap;
  setFile: (nodeId: string, file: File | null) => void;
  clearAll: () => void;
};

const defaultCtx: Ctx = {
  staged: new Map(),
  setFile: () => {},
  clearAll: () => {},
};

export const StagedUploadsContext = React.createContext<Ctx>(defaultCtx);

export const StagedUploadsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Use a single Map reference to avoid re-renders on every set
  const stagedRef = React.useRef<StagedMap>(new Map());
  const [, force] = React.useReducer((x) => x + 1, 0); // small trick to re-render when needed

  const setFile = React.useCallback((nodeId: string, file: File | null) => {
    const map = stagedRef.current;
    if (file) map.set(nodeId, file);
    else map.delete(nodeId);
    force(); // re-render providers/consumers that read .staged
  }, []);

  const clearAll = React.useCallback(() => {
    stagedRef.current.clear();
    force();
  }, []);

  const value = React.useMemo(
    () => ({ staged: stagedRef.current, setFile, clearAll }),
    [setFile, clearAll]
  );

  return (
    <StagedUploadsContext.Provider value={value}>
      {children}
    </StagedUploadsContext.Provider>
  );
};

// Convenience hook
export function useStagedUploads() {
  return React.useContext(StagedUploadsContext);
}
